import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import BasicMap from "components/BasicMap";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { SiteManagerAccessStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { useTenant } from "hooks/useTenant";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";

import {
  SITE_MANAGER_ACCESS_REQUEST_SUFFIX,
  SITE_MANAGER_ACCESS_TOOLTIP,
} from "../../siteManagerAccess";
import { facilityAddressLines } from "./facilityAddressLines";
import { useWasteFacility } from "./useWasteFacility";

export const FacilityDetailsTile = () => {
  return (
    <DashboardTile size={{ sm: 12, md: 7 }}>
      <FacilityDetailsTileContent />
    </DashboardTile>
  );
};

const FacilityDetailsTileContent = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { tenantId } = useTenant();
  const location = useLocation();
  // Access chips surface only with the v2 manifest views (staff always v2),
  // matching the facilities-list column — v1 tenants don't see site-manager
  // access anywhere else.
  const showManifestViewsV2 = useManifestViewsV2();

  const { data } = useWasteFacility(facilityId);
  const { latitude, longitude, name, streetAddress1, streetAddress2, city, state, zip, county } =
    data.facility || {};

  // Get EPA ID statuses which includes both database EPA IDs
  // and mapped EPA IDs from epaIdGroupMappings
  const epaIdStatuses = data.facility?.epaIdStatuses ?? [];

  const hasLatAndLong = typeof latitude === "number" && typeof longitude === "number";

  useBreadcrumb({ label: name ?? "" });

  return (
    <>
      <DashboardTile.Header
        title="Facility Details"
        infoTooltip="See basic information about your facility. If any of this information is incorrect or needs to be updated, please reach out to Encamp."
      />
      <DashboardTile.ContentArea>
        <Grid container spacing={5}>
          {hasLatAndLong && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={2}>
                <Box height={"262px"}>
                  <BasicMap latitude={latitude} longitude={longitude} />
                </Box>
                <Typography variant="caption">{[latitude, longitude].join(", ")}</Typography>
              </Stack>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: hasLatAndLong ? 6 : 12 }}>
            <Stack spacing={2}>
              {epaIdStatuses.length > 0 && (
                <Stack alignItems="flex-start" spacing={1} gap={1}>
                  {epaIdStatuses.length > 1 && (
                    <Alert severity="warning" icon={<WarningAmber />}>
                      There are multiple EPA IDs associated with this facility
                    </Alert>
                  )}
                  <Stack spacing={1}>
                    {epaIdStatuses.map((epaIdStatus) => (
                      <Stack
                        // useFlexGap so spacing works with flexWrap: Stack's
                        // default margin-based spacing gives wrapped lines no
                        // vertical gap and a stray left indent.
                        spacing={2}
                        rowGap={1}
                        useFlexGap
                        key={epaIdStatus.epaId}
                        alignItems="center"
                        flexWrap="wrap"
                        direction="row"
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          component="a"
                          href={`https://rcrapublic.epa.gov/rcra-hwip/search/results/site/${epaIdStatus.epaId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {epaIdStatus.epaId}
                          <Tooltip title="View RCRA Hazardous Waste Information Portal in new tab">
                            <IconButton>
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        {epaIdStatus.isInactive && (
                          <Chip
                            label="EPA ID is inactive"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {showManifestViewsV2 &&
                          epaIdStatus.siteManagerAccess === SiteManagerAccessStatus.Granted && (
                            <Tooltip title={SITE_MANAGER_ACCESS_TOOLTIP}>
                              <Chip
                                label="RCRAInfo site manager access"
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        {showManifestViewsV2 &&
                          epaIdStatus.siteManagerAccess === SiteManagerAccessStatus.None && (
                            <Tooltip
                              title={`${SITE_MANAGER_ACCESS_TOOLTIP} ${SITE_MANAGER_ACCESS_REQUEST_SUFFIX}`}
                            >
                              <Chip
                                label="No RCRAInfo site manager access"
                                color="warning"
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        {!epaIdStatus.isPrimaryForFacility && epaIdStatus.primaryFacility && (
                          <Typography variant="body2" color="textSecondary">
                            (Waste reported through{" "}
                            <Link
                              component={RouterLink}
                              to={`/o/${tenantId}/waste/facilities/${epaIdStatus.primaryFacility.id}${location.search}`}
                            >
                              {epaIdStatus.primaryFacility.name}
                            </Link>
                            )
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}

              <Typography variant="h4">{name}</Typography>
              <Box data-testid="facility-address">
                {facilityAddressLines({ streetAddress1, streetAddress2, city, state, zip }).map(
                  (line) => (
                    <Typography key={line} variant="body1">
                      {line}
                    </Typography>
                  )
                )}
              </Box>
              {county && (
                <Typography variant="body1">
                  {county.endsWith(" County") ? county : `${county} County`}
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DashboardTile.ContentArea>
    </>
  );
};
